import { useEffect, useState } from "react";
import { PortalModel } from "@apps/mf-overview/src/server/portals/model";

const preloadedSet = new Set<string>();

/**
 * We defer both loading and display of portal images in order to increase
 * performance of the initial user-area transition, which can be quite rough
 * due to the wast area it repaints.
 */
export function usePreloadingThumbnails(portals: PortalModel[]): boolean {
  const thumbnailUrls =
    (portals
      ?.map((it) => (it.thumbnail ? `${it.thumbnail}?h=80` : undefined))
      .filter((it) => !!it) as string[]) ?? [];

  const [showThumbnails, setShowThumbnails] = useState(() =>
    thumbnailUrls.every((url) => preloadedSet.has(url))
  );

  useEffect(() => {
    setShowThumbnails(thumbnailUrls.every((url) => preloadedSet.has(url)));
  }, [thumbnailUrls.join("-")]);

  useEffect(() => {
    // In case all thumbnils already are available, then theres no need to
    // preload them again.
    if (showThumbnails) {
      return;
    }

    const startedAt = Date.now();
    let preloadTimeout: number | undefined;
    let showTimeout: number | undefined;

    const preloadImage = async (thumbnailUrl: string): Promise<void> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = thumbnailUrl;
        img.addEventListener("load", () => {
          preloadedSet.add(thumbnailUrl);
          resolve();
        });
      });
    };

    const preloadPromises = thumbnailUrls.map(preloadImage);

    Promise.all(preloadPromises).then(() => {
      const elapsed = Date.now() - startedAt;
      const waitFor = Math.max(0, elapsed);

      showTimeout = window.setTimeout(() => {
        setShowThumbnails(true);
      }, waitFor);
    });

    return (): void => {
      window.clearTimeout(preloadTimeout);
      window.clearTimeout(showTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showThumbnails]);

  return showThumbnails;
}
